/**
 * Bottom-of-message "Related resources" row.
 *
 * Phase 3 of the Resources feature. Renders below the assistant's
 * answer when the turn carries any resources (set up by an admin on
 * the Resources page, attached to cited documents).
 *
 * Behaviour:
 *   - kind === 'url'  → <a target=_blank> opens the link.
 *   - kind === 'file' → <a target=_blank> to /api/resources/<id>/file.
 *                       The dashboard uses session-cookie auth so a
 *                       plain <a href> works (unlike the widget which
 *                       uses Bearer tokens and needs a fetch-then-blob
 *                       trick). The server sets Content-Disposition:
 *                       inline so PDFs / images render in the new tab
 *                       and office docs fall through to a download.
 *
 * Visually parallels the .message-sources pill row at the top of the
 * bubble, but with a softer separator so the surface reads as
 * "supplementary" rather than "this is what the answer was drawn from".
 */
import { ExternalLink, FileText, Link as LinkIcon, Image as ImageIcon } from 'lucide-react';
import type { MessageResource } from '../api/conversation';
import { useT } from '../I18nContext';

interface Props {
  resources: MessageResource[];
}

export function MessageResources({ resources }: Props) {
  const t = useT();
  if (!resources || resources.length === 0) return null;
  return (
    <div className="message-resources" role="group" aria-label={t('related_resources.label')}>
      <div className="message-resources-label">
        {t('related_resources.label')}
      </div>
      <div className="message-resources-pills">
        {resources.map((r) => (
          <ResourcePill key={r.id} resource={r} />
        ))}
      </div>
    </div>
  );
}

function ResourcePill({ resource }: { resource: MessageResource }) {
  const href = resource.kind === 'url'
    ? (resource.url || '#')
    : `/api/resources/${encodeURIComponent(resource.id)}/file`;
  const Icon = pickIcon(resource);
  return (
    <a
      className="message-resources-pill"
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      title={resource.kind === 'url'
        ? resource.url
        : (resource.original_filename || resource.title)}
    >
      <Icon size={12} className="message-resources-pill-icon" aria-hidden />
      <span className="message-resources-pill-title">{resource.title}</span>
      <ExternalLink
        size={11}
        className="message-resources-pill-out"
        aria-hidden
      />
    </a>
  );
}

function pickIcon(r: MessageResource) {
  if (r.kind === 'url') return LinkIcon;
  const mime = r.mime_type || '';
  if (mime.startsWith('image/')) return ImageIcon;
  return FileText;
}
